// VariantEditorMobile.jsx — éditeur de déclinaisons tactile (profil e-commerce).
// Pendant mobile de src/gestion-produits/VariantManager.jsx : même logique métier
// (axes cartésiens → déclinaisons), mais UI en cartes empilées adaptée au tactile.
// La logique de génération est RÉUTILISÉE telle quelle depuis VariantManager.jsx.
import React, { useMemo, useRef, useState } from "react";
import { Ico, fmt } from "./productsShared";
import {
  cartesianCombinations,
  comboKey,
  comboLabel,
} from "../../gestion-produits/VariantManager.jsx";

const MAX_AXES = 3;
const MAX_COMBOS = 200;

// Une ligne d'axe : nom + valeurs (chips). Garde son propre brouillon de saisie.
function AxisRow({ axis, index, canRemove, onName, onValues, onRemove }) {
  const [draft, setDraft] = useState("");

  const commitDraft = () => {
    const parts = draft
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const next = Array.from(new Set([...(axis.values || []), ...parts]));
    onValues(index, next);
    setDraft("");
  };

  const removeValue = (value) => {
    onValues(
      index,
      (axis.values || []).filter((v) => v !== value)
    );
  };

  return (
    <div className="vax">
      <div className="vax__top">
        <input
          className="ctrl vax__name"
          placeholder="Nom de l'axe (ex : Taille)"
          value={axis.name}
          onChange={(e) => onName(index, e.target.value)}
        />
        {canRemove ? (
          <button
            type="button"
            className="iconbtn vax__del"
            onClick={() => onRemove(index)}
            aria-label="Supprimer l'axe"
          >
            <Ico name="trash-2" />
          </button>
        ) : null}
      </div>

      {(axis.values || []).length ? (
        <div className="vax__chips">
          {axis.values.map((value) => (
            <span className="vchip" key={value}>
              {value}
              <button
                type="button"
                className="vchip__x"
                onClick={() => removeValue(value)}
                aria-label={`Retirer ${value}`}
              >
                <Ico name="x" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="input-wrap">
        <input
          className="ctrl"
          placeholder="Valeur puis Entrée (ex : S, M, L)"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commitDraft();
            }
          }}
          onBlur={commitDraft}
        />
        {draft.trim() ? (
          <span
            className="suffix"
            style={{ pointerEvents: "auto", color: "var(--blue)" }}
            onClick={commitDraft}
          >
            Ajouter
          </span>
        ) : null}
      </div>
    </div>
  );
}

// Carte d'une déclinaison générée : prix, stock, SKU/code-barres, image.
function VariantCard({
  row,
  onChange,
  onRemove,
  onImage,
  existingProduct,
  isExisting,
  onSelectExisting,
}) {
  const fileRef = useRef(null);
  const num = (v) => (v === "" || v === null || v === undefined ? "" : v);

  return (
    <div className="vcard">
      <div className="vcard__h">
        <span className="vtag">{row.label}</span>
        <button
          type="button"
          className="vcard__del"
          onClick={() => onRemove(row.key)}
          disabled={isExisting}
          aria-label="Supprimer la déclinaison"
        >
          <Ico name="trash-2" />
        </button>
      </div>

      {existingProduct ? (
        <button
          type="button"
          className={isExisting ? "btn btn--primary btn--block" : "btn btn--ghost btn--block"}
          style={{ marginBottom: 12 }}
          onClick={() => onSelectExisting(row.key)}
        >
          <Ico name={isExisting ? "check-circle-2" : "circle"} />
          {isExisting ? "Produit actuel conservé ici" : "C'est le produit actuel"}
        </button>
      ) : null}

      <div className="frow">
        <div className="field">
          <div className="field__l">
            Prix de vente<span className="req">*</span>
          </div>
          <div className="input-wrap">
            <input
              className="ctrl"
              inputMode="numeric"
              placeholder="0"
              value={num(row.sales_price)}
              onChange={(e) => onChange(row.key, "sales_price", e.target.value)}
            />
            <span className="suffix">F</span>
          </div>
        </div>
        <div className="field">
          <div className="field__l">Prix d'achat</div>
          <div className="input-wrap">
            <input
              className="ctrl"
              inputMode="numeric"
              placeholder="0"
              value={num(row.purchase_price)}
              onChange={(e) =>
                onChange(row.key, "purchase_price", e.target.value)
              }
            />
            <span className="suffix">F</span>
          </div>
        </div>
      </div>

      <div className="frow">
        <div className="field">
          <div className="field__l">
            {isExisting ? "Stock actuel conservé" : "Stock initial"}
          </div>
          <input
            className="ctrl"
            inputMode="numeric"
            placeholder="0"
            value={
              isExisting
                ? num(existingProduct?.current_stock)
                : num(row.opening_stock)
            }
            disabled={isExisting}
            onChange={(e) => onChange(row.key, "opening_stock", e.target.value)}
          />
        </div>
        <div className="field">
          <div className="field__l">Seuil alerte</div>
          <input
            className="ctrl"
            inputMode="numeric"
            placeholder="0"
            value={num(row.stock_quantitiy_alert)}
            onChange={(e) =>
              onChange(row.key, "stock_quantitiy_alert", e.target.value)
            }
          />
        </div>
      </div>

      <div className="frow">
        <div className="field">
          <div className="field__l">
            SKU<span className="field__hint">auto</span>
          </div>
          <input
            className="ctrl"
            placeholder="Auto si vide"
            value={
              isExisting ? existingProduct?.item_code || "" : row.item_code || ""
            }
            disabled={isExisting}
            onChange={(e) => onChange(row.key, "item_code", e.target.value)}
          />
        </div>
        <div className="field">
          <div className="field__l">
            Code-barres<span className="field__hint">= SKU</span>
          </div>
          <input
            className="ctrl"
            placeholder="= SKU si vide"
            value={
              isExisting
                ? existingProduct?.barcode_value || existingProduct?.item_code || ""
                : row.barcode_value || ""
            }
            disabled={isExisting}
            onChange={(e) => onChange(row.key, "barcode_value", e.target.value)}
          />
        </div>
      </div>

      <button
        type="button"
        className="btn btn--ghost btn--block vcard__img"
        disabled={isExisting}
        onClick={() => fileRef.current?.click()}
      >
        <Ico name="camera" />
        {isExisting
          ? "Image actuelle conservée"
          : row.imageFile
          ? "Image ajoutée · changer"
          : "Ajouter une image (option)"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onImage(row.key, file);
          if (fileRef.current) fileRef.current.value = "";
        }}
      />
    </div>
  );
}

export default function VariantEditorMobile({
  axes = [],
  setAxes,
  variants = [],
  setVariants,
  getDefaults,
  toast,
  existingProduct = null,
  existingVariantKey = null,
  onExistingVariantChange,
}) {
  const previewCount = useMemo(
    () => cartesianCombinations(axes).length,
    [axes]
  );

  const updateAxisName = (index, name) =>
    setAxes((prev) => prev.map((a, i) => (i === index ? { ...a, name } : a)));
  const updateAxisValues = (index, values) =>
    setAxes((prev) => prev.map((a, i) => (i === index ? { ...a, values } : a)));
  const addAxis = () => {
    if (axes.length >= MAX_AXES) {
      toast && toast("3 axes maximum (ex : Taille, Couleur, Matière)", "alert");
      return;
    }
    setAxes((prev) => [...prev, { name: "", values: [] }]);
  };
  const removeAxis = (index) =>
    setAxes((prev) => prev.filter((_, i) => i !== index));

  const generate = () => {
    const combos = cartesianCombinations(axes);
    if (combos.length === 0) {
      toast &&
        toast("Définissez au moins un axe avec des valeurs (ex : S, M, L)", "alert");
      return;
    }
    if (combos.length > MAX_COMBOS) {
      toast &&
        toast(
          `${combos.length} déclinaisons : trop nombreuses (max ${MAX_COMBOS})`,
          "alert"
        );
      return;
    }
    const defaults =
      (typeof getDefaults === "function" ? getDefaults() : null) || {};
    if (
      existingVariantKey &&
      !combos.some((combo) => comboKey(combo) === existingVariantKey)
    ) {
      onExistingVariantChange?.(null);
    }
    setVariants((prev) => {
      const existingByKey = (prev || []).reduce((map, row) => {
        map[row.key] = row;
        return map;
      }, {});
      return combos.map((combo) => {
        const key = comboKey(combo);
        const existing = existingByKey[key];
        if (existing) {
          return { ...existing, variant_values: combo, label: comboLabel(combo) };
        }
        return {
          key,
          variant_values: combo,
          label: comboLabel(combo),
          item_code: "",
          barcode_value: "",
          purchase_price: defaults.purchase_price ?? "",
          sales_price: defaults.sales_price ?? "",
          opening_stock: 0,
          stock_quantitiy_alert: defaults.stock_quantitiy_alert ?? 0,
          tax_id: defaults.tax_id ?? null,
          imageFile: null,
        };
      });
    });
    toast && toast(`${combos.length} déclinaison(s) générée(s)`);
  };

  const updateVariant = (key, field, value) =>
    setVariants((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  const setVariantImage = (key, file) => updateVariant(key, "imageFile", file);
  const removeVariant = (key) => {
    if (existingProduct && key === existingVariantKey) {
      toast &&
        toast(
          "Choisissez une autre déclinaison pour le produit actuel avant de supprimer celle-ci",
          "alert"
        );
      return;
    }
    setVariants((prev) => prev.filter((row) => row.key !== key));
  };

  return (
    <div className="veditor">
      <div className="fgroup">
        <div className="fgroup__h">
          <div className="ico">
            <Ico name="sliders-horizontal" />
          </div>
          <div>
            <h3>Axes de déclinaison</h3>
            <p>Ex : Taille (S, M, L) puis Couleur — génère les combinaisons.</p>
          </div>
        </div>

        {axes.map((axis, index) => (
          <AxisRow
            key={index}
            axis={axis}
            index={index}
            canRemove={axes.length > 1}
            onName={updateAxisName}
            onValues={updateAxisValues}
            onRemove={removeAxis}
          />
        ))}

        <div className="frow" style={{ marginTop: 4 }}>
          <button type="button" className="btn btn--ghost" onClick={addAxis}>
            <Ico name="plus" />
            Ajouter un axe
          </button>
          <button type="button" className="btn btn--primary" onClick={generate}>
            <Ico name="layers" />
            Générer{previewCount ? ` (${previewCount})` : ""}
          </button>
        </div>
      </div>

      <div className="fgroup">
        <div className="fgroup__h">
          <div className="ico">
            <Ico name="boxes" />
          </div>
          <div>
            <h3>Déclinaisons{variants.length ? ` (${variants.length})` : ""}</h3>
            <p>Prix et stock par déclinaison. SKU/code-barres auto si vides.</p>
          </div>
        </div>

        {existingProduct ? (
          <div
            className="vempty"
            style={{ marginBottom: 14, borderStyle: "solid", textAlign: "left" }}
          >
            <Ico name="info" />
            <b>Associez le produit actuel à une déclinaison</b>
            <span>
              Son identifiant, son stock ({fmt(existingProduct.current_stock || 0)}),
              ses codes et tout son historique seront conservés.
            </span>
          </div>
        ) : null}

        {variants.length ? (
          variants.map((row) => (
            <VariantCard
              key={row.key}
              row={row}
              onChange={updateVariant}
              onRemove={removeVariant}
              onImage={setVariantImage}
              existingProduct={existingProduct}
              isExisting={row.key === existingVariantKey}
              onSelectExisting={onExistingVariantChange}
            />
          ))
        ) : (
          <div className="vempty">
            <Ico name="layers" />
            <b>Aucune déclinaison</b>
            <span>
              Définissez les axes puis appuyez sur « Générer » ({fmt(previewCount)}{" "}
              possible{previewCount > 1 ? "s" : ""}).
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
